import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

export const PROFILE_PDF_URL = `${API}/profile.pdf`;

export interface AuthUser {
  user_id: string;
  email: string;
  name: string;
  picture: string;
  role: "visitor" | "admin";
  favourites: string[];
}

export async function authExchangeSession(sessionId: string): Promise<AuthUser> {
  const { data } = await axios.post<AuthUser>(`${API}/auth/session`, { session_id: sessionId });
  return data;
}

export async function authMe(): Promise<AuthUser> {
  const { data } = await axios.get<AuthUser>(`${API}/auth/me`);
  return data;
}

export async function authLogout() {
  await axios.post(`${API}/auth/logout`);
}

export async function fetchFavourites(): Promise<Project[]> {
  const { data } = await axios.get<Project[]>(`${API}/me/favourites`);
  return data;
}

export async function addFavourite(slug: string): Promise<AuthUser> {
  const { data } = await axios.post<AuthUser>(`${API}/me/favourites`, { slug });
  return data;
}

export async function removeFavourite(slug: string): Promise<AuthUser> {
  const { data } = await axios.delete<AuthUser>(`${API}/me/favourites/${slug}`);
  return data;
}

export async function fetchMyEnquiries(): Promise<Lead[]> {
  const { data } = await axios.get<Lead[]>(`${API}/me/enquiries`);
  return data;
}

export interface Project {
  slug: string;
  title: string;
  city: string;
  city_slug: string;
  property_type: string;
  budget_band: string;
  budget_label: string;
  timeline_days: number;
  year: number;
  area: string;
  materials: string[];
  summary: string;
  honesty: string;
  render_url: string;
  reality_url: string;
  pairs: RoomPair[];
  is_real: boolean;
  stage: "delivered" | "in_execution";
  sort: number;
}

export interface RoomPair {
  room: string;
  render_url: string;
  reality_url: string;
  note: string;
}

export interface Lead {
  _id: string;
  name: string;
  phone: string;
  email: string;
  city: string;
  property_type: string;
  budget_band: string;
  timeline: string;
  message: string;
  status: string;
  created_at: string;
}

export interface LeadInput {
  name: string;
  phone: string;
  email?: string;
  city: string;
  property_type: string;
  budget_band: string;
  timeline: string;
  message?: string;
  consent: boolean;
}

export function formatApiErrorDetail(detail: any): string {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e: any) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" · ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export async function fetchProjects(): Promise<Project[]> {
  const { data } = await axios.get<Project[]>(`${API}/projects`);
  return data;
}

export async function fetchProject(slug: string): Promise<Project> {
  const { data } = await axios.get<Project>(`${API}/projects/${slug}`);
  return data;
}

export async function submitLead(payload: LeadInput): Promise<{ id: string; whatsapp_url: string }> {
  const { data } = await axios.post(`${API}/leads`, payload);
  return data;
}

export async function adminLogin(password: string): Promise<{ token: string; email: string }> {
  const { data } = await axios.post(`${API}/admin/login`, { password });
  return data;
}

// token = password-login JWT; null = rely on the Google session cookie.
const adminHeaders = (token: string | null) => (token ? { Authorization: `Bearer ${token}` } : {});

export async function fetchLeads(token: string | null) {
  const { data } = await axios.get(`${API}/admin/leads`, { headers: adminHeaders(token) });
  return data as { leads: Lead[]; stats: Record<string, any> };
}

export async function updateLeadStatus(token: string | null, id: string, status: string) {
  const { data } = await axios.patch(`${API}/admin/leads/${id}/status`, { status }, { headers: adminHeaders(token) });
  return data;
}
